import { useState, type FormEvent } from 'react';
import { api } from '@/lib/api';
import { errorMessage } from '@/components/Toast';
import { Button, Card, CardHead, Field, Input, PageHeader, Select, Textarea } from '@/components/ui';

type Category = 'GENERAL' | 'SUPPORT' | 'ORDERS';

const CATEGORIES: { value: Category; label: string }[] = [
  { value: 'GENERAL', label: 'General' },
  { value: 'SUPPORT', label: 'Support' },
  { value: 'ORDERS', label: 'Orders' },
];

/** Keeps the honeypot off-screen for humans (no sr-only utility in index.css). */
const HONEYPOT_STYLE = {
  position: 'absolute',
  left: '-9999px',
  width: 1,
  height: 1,
  overflow: 'hidden',
} as const;

export function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', website: '' });
  const [category, setCategory] = useState<Category>('GENERAL');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.contact.send({
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || undefined,
        message: form.message.trim(),
        category,
        website: form.website,
      });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader title="Contact" description="Questions, support requests, or feedback — send us a note." />

      <Card style={{ maxWidth: 620 }}>
        <CardHead title="Send a message" />
        <div className="card-pad">
          {sent ? (
            <p>Thanks — your message has been sent. We&rsquo;ll get back to you soon.</p>
          ) : (
            <form className="stack gap-4" onSubmit={onSubmit}>
              <Field label="Name">
                <Input value={form.name} onChange={set('name')} autoComplete="name" placeholder="Jane Doe" required autoFocus />
              </Field>
              <Field label="Email">
                <Input type="email" value={form.email} onChange={set('email')} autoComplete="email" placeholder="you@firm.com" required />
              </Field>
              <Field label="Subject" hint="Optional.">
                <Input value={form.subject} onChange={set('subject')} placeholder="What is this about?" />
              </Field>
              <Field label="Category">
                <Select value={category} onChange={(e) => setCategory(e.target.value as Category)}>
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </Select>
              </Field>
              <Field label="Message" error={error ?? undefined}>
                <Textarea value={form.message} onChange={set('message')} placeholder="How can we help?" required rows={6} />
              </Field>

              {/* Honeypot: hidden from humans; bots that fill it are dropped server-side. */}
              <div aria-hidden="true" style={HONEYPOT_STYLE}>
                <label htmlFor="contact-website">Website</label>
                <input
                  id="contact-website"
                  name="website"
                  type="text"
                  value={form.website}
                  onChange={set('website')}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <Button type="submit" loading={busy}>Send message</Button>
            </form>
          )}
        </div>
      </Card>
    </>
  );
}
